import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimContractSubObjectCreateInput } from 'gql/graphql';
import { useSetIsWorkOrderChangeRequired } from 'pages/Project/BimProject/hooks/useSetIsWorkOrderChangeRequired';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { bimCreateWorkOrderSubObjectGql } from '../../bimWorkOrderQueries';

const requestId = 'bimCreateWorkOrderSubObject';

export const useBimCreateWorkOrderSubObject = (workOrderId?: number, onSuccess?: (newSubObjectId?: number) => void) => {
    const dispatch = useDispatch();

    const [bimCreateWorkOrderSubObjectReset] = useResetStoreData({ requestId });

    const [
        bimCreateWorkOrderSubObjectData,
        bimCreateWorkOrderSubObjectStatus,
        bimCreateWorkOrderSubObjectError,
        bimCreateWorkOrderSubObjectRequest,
    ] = useRequest({
        requestId,
        query: bimCreateWorkOrderSubObjectGql,
        withEffect: false,
    });

    useSetIsWorkOrderChangeRequired({
        useData: { isWorkOrderChangeRequired: true },
        status: bimCreateWorkOrderSubObjectStatus,
    });

    const bimCreateWorkOrderSubObjectMakeRequest = useCallback(
        (input: BimContractSubObjectCreateInput) => {
            dispatch(bimCreateWorkOrderSubObjectRequest({ workOrderId, input }));
        },
        [bimCreateWorkOrderSubObjectRequest, dispatch, workOrderId],
    );

    useEffect(() => {
        bimCreateWorkOrderSubObjectReset(undefined);
    }, [bimCreateWorkOrderSubObjectReset]);

    useEffect(() => {
        if (
            bimCreateWorkOrderSubObjectStatus === RequestStatus.SUCCESS &&
            bimCreateWorkOrderSubObjectData?.bimCreateWorkOrderSubObject?.data?.id
        ) {
            bimCreateWorkOrderSubObjectReset(undefined);
            onSuccess?.(bimCreateWorkOrderSubObjectData?.bimCreateWorkOrderSubObject?.data?.id);
        }
    }, [
        bimCreateWorkOrderSubObjectData?.bimCreateWorkOrderSubObject?.data?.id,
        bimCreateWorkOrderSubObjectReset,
        bimCreateWorkOrderSubObjectStatus,
        onSuccess,
    ]);

    return {
        bimCreateWorkOrderSubObjectData,
        bimCreateWorkOrderSubObjectStatus,
        bimCreateWorkOrderSubObjectError,
        bimCreateWorkOrderSubObjectMakeRequest,
    };
};
